import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useTenantStore } from "../../stores/tenant";
import { useToastStore } from "../../stores/toast";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { TenantCreateModal } from "../../components/tenants/tenant-create-modal";

interface StoredNumber {
  id: string;
  tenantId: string;
  e164: string;
  twilioSid: string;
  status: "active" | "released";
}

// Numéro possédé sur le compte Twilio (cf. GET /api/numbers/owned).
interface OwnedNumber {
  sid: string;
  e164: string;
  friendlyName: string;
  assigned: boolean;
}

function formatE164(e164: string): string {
  const m = e164.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  return m ? `+1 ${m[1]} ${m[2]} ${m[3]}` : e164;
}

export function TenantsList() {
  const tenants = useTenantStore((s) => s.available);
  const loadAvailable = useTenantStore((s) => s.loadAvailable);
  const setTenant = useTenantStore((s) => s.setTenant);
  const push = useToastStore((s) => s.push);

  const [createOpen, setCreateOpen] = useState(false);
  const [numbers, setNumbers] = useState<StoredNumber[]>([]);
  const [owned, setOwned] = useState<OwnedNumber[]>([]);
  // Ligne dont le sélecteur « connecter un numéro » est ouvert + numéro choisi.
  const [connecting, setConnecting] = useState<string | null>(null);
  const [selectedSid, setSelectedSid] = useState("");
  const [busy, setBusy] = useState(false);

  const loadNumbers = () => {
    api<StoredNumber[]>("/api/numbers").then(setNumbers).catch(() => setNumbers([]));
    api<OwnedNumber[]>("/api/numbers/owned").then(setOwned).catch(() => setOwned([]));
  };
  useEffect(loadNumbers, []);

  const freeNumbers = owned.filter((o) => !o.assigned);
  const numbersOf = (tenantId: string) => numbers.filter((n) => n.tenantId === tenantId && n.status === "active");

  const connect = async (tenantId: string) => {
    const num = freeNumbers.find((o) => o.sid === selectedSid);
    if (!num || busy) return;
    setBusy(true);
    try {
      await api("/api/numbers/assign", {
        method: "POST",
        body: JSON.stringify({ e164: num.e164, twilioSid: num.sid, tenantId }),
      });
      push("success", `${formatE164(num.e164)} connecté (webhook configuré automatiquement)`);
      setConnecting(null);
      setSelectedSid("");
      loadNumbers();
    } catch {
      push("danger", "Échec de la connexion du numéro");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async (n: StoredNumber) => {
    if (busy) return;
    if (!window.confirm(`Déconnecter ${formatE164(n.e164)} ? Le numéro reste dans le compte Twilio et les appels entrants ne seront plus répondus.`)) return;
    setBusy(true);
    try {
      await api(`/api/numbers/${n.id}/unassign`, { method: "POST" });
      push("success", `${formatE164(n.e164)} déconnecté`);
      loadNumbers();
    } catch {
      push("danger", "Échec de la déconnexion");
    } finally {
      setBusy(false);
    }
  };

  const onCreated = async (tenantId: string) => {
    await loadAvailable();
    loadNumbers();
    setTenant(tenantId);
  };

  return (
    <div>
      <header className="page-header">
        <div className="page-header__titles">
          <h1 className="page-header__title">Établissements</h1>
          <p className="page-header__subtitle">
            Chaque établissement a son numéro, sa voix, ses horaires et son métier.
            {" "}
            {freeNumbers.length > 0 ? (
              <>
                {freeNumbers.length} numéro{freeNumbers.length > 1 ? "s" : ""} Twilio libre
                {freeNumbers.length > 1 ? "s" : ""} —{" "}
              </>
            ) : (
              <>Aucun numéro Twilio libre — </>
            )}
            <Link to="/numbers">gérer / acheter des numéros</Link>.
          </p>
        </div>
        <div className="page-header__actions">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            Nouvel établissement
          </Button>
        </div>
      </header>

      <TenantCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        freeNumbers={freeNumbers}
        onCreated={onCreated}
      />

      <table className="table">
        <thead>
          <tr>
            <th>Nom</th>
            <th>Type</th>
            <th>Langue</th>
            <th>Numéro</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {tenants.length === 0 ? (
            <tr><td colSpan={5} className="table__empty">Aucun établissement — clique « Nouvel établissement ».</td></tr>
          ) : (
            tenants.map((t) => {
              const linked = numbersOf(t.id);
              return (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td><Badge variant="primary">{t.type}</Badge></td>
                  <td>{t.language}</td>
                  <td>
                    <span className="tenant-num">
                      {linked.map((n) => (
                        <span key={n.id} className="tenant-num__item">
                          {formatE164(n.e164)}
                          <button
                            type="button"
                            className="tenant-num__remove"
                            title="Déconnecter ce numéro (il reste dans le compte Twilio)"
                            aria-label={`Déconnecter ${formatE164(n.e164)}`}
                            onClick={() => disconnect(n)}
                            disabled={busy}
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                      {connecting === t.id ? (
                        <span className="tenant-num__picker">
                          <select
                            className="select"
                            value={selectedSid}
                            onChange={(e) => setSelectedSid(e.target.value)}
                          >
                            <option value="">Numéro libre…</option>
                            {freeNumbers.map((o) => (
                              <option key={o.sid} value={o.sid}>{formatE164(o.e164)}</option>
                            ))}
                          </select>
                          <button type="button" className="btn btn--primary btn--sm" onClick={() => connect(t.id)} disabled={!selectedSid || busy}>
                            Lier
                          </button>
                          <button type="button" className="tenant-num__connect" onClick={() => { setConnecting(null); setSelectedSid(""); }}>
                            Annuler
                          </button>
                        </span>
                      ) : linked.length === 0 ? (
                        <button
                          type="button"
                          className="tenant-num__connect"
                          onClick={() => { setConnecting(t.id); setSelectedSid(""); }}
                          disabled={freeNumbers.length === 0 || busy}
                          title={freeNumbers.length === 0 ? "Aucun numéro libre dans le compte Twilio" : "Connecter un numéro du compte Twilio (webhook configuré automatiquement)"}
                        >
                          {freeNumbers.length === 0 ? "Aucun numéro" : "+ Connecter"}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="tenant-num__connect"
                          onClick={() => { setConnecting(t.id); setSelectedSid(""); }}
                          disabled={freeNumbers.length === 0 || busy}
                          title="Connecter un numéro supplémentaire"
                          aria-label="Connecter un numéro supplémentaire"
                        >
                          +
                        </button>
                      )}
                    </span>
                  </td>
                  <td className="table__actions">
                    <Link to={`/tenants/${t.id}/settings`}>Configurer</Link>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
